import type { NextPage, NextPageContext } from "next";
import { useRouter } from "next/router";
import { styled } from "@mui/material";
import AudioDashboard from "@audio/AudioDashboard";
import DynamicContent from "@pageContent/DynamicContent";
import OopsPage from "@pageContent/OopsPage";
import PageContainer from "@pageContent/common/PageContainer";
import { AudioType, PAGE_CONTAINER, audioDashboardHeightMd, tableNames, transitions } from "@constants/consts";
import { colors } from "@constants/cssVariables";
import { fetchCurrentShow } from "@utils/audio/audioApi";
import { Show } from "@utils/audio/dataModels";
import { useAudioEntry } from "@utils/audio/useAudioEntry";
import { createRedirectResponse } from "@utils/common/createRedirectResponse";
import DynamoDBUtils from "@utils/common/dynamo";
import getHostConfig from "@utils/common/getHostConfig";
import usePageView from "@utils/common/usePageView";

export const PlaceHolderAudioDashboardSkeleton = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  overflow: "hidden",
  background: colors.neutral10,
  transition: transitions.hamburgerMenu,
  height: 333,

  [theme.breakpoints.up("sm")]: {
    height: 378,
  },

  [theme.breakpoints.up("md")]: {
    height: audioDashboardHeightMd,
  },

  [theme.breakpoints.up("lg")]: {
    height: 410,
  },
}));

const StationPage: NextPage<{ initialStation?: AudioStationEntry | null; initialShow?: Show | null }> = ({
  initialStation,
  initialShow,
}) => {
  const {
    query: { slug },
  } = useRouter();

  const {
    isLoading,
    data: station,
    isError,
    error,
  } = useAudioEntry<AudioStationEntry>({
    audioType: AudioType.STATION,
    slug: slug as string,
    initialData: initialStation,
  });

  usePageView({ isLoading, error, data: station });

  if (!station) {
    if (isLoading) return <PlaceHolderAudioDashboardSkeleton />;
    return <OopsPage is404={!isError} error={error} />;
  }

  return (
    <>
      <AudioDashboard station={station} isOnStationPage initialShow={initialShow} />
      <PageContainer id={PAGE_CONTAINER}>
        {station.items?.map((item) => <DynamicContent key={item.id} {...item} />)}
      </PageContainer>
    </>
  );
};

StationPage.getInitialProps = async ({ req, res, query }: NextPageContext) => {
  if (!res || !req) return {};

  const { mainStationSlug } = getHostConfig();
  const { slug } = query;

  if (!mainStationSlug || !slug) {
    return createRedirectResponse({ res, query, url: "/" });
  }

  const [initialStation, initialShow] = await Promise.all([
    DynamoDBUtils.getItemBySlug<AudioStationEntry>(tableNames.audio, slug as string),
    fetchCurrentShow(slug as string),
  ]);

  return { initialStation, initialShow };
};

export default StationPage;
